import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Input,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { updateForm, getHostedFormUrl } from "../../../redux/actions/formsAction";
import { useFormDetail } from "./FormDetailContext";
import {
  CONTACT_REQUIREMENT_OPTIONS,
  IDENTITY_FIELD_NAMES,
  contactRequirementToValidation,
  createFieldFromPreset,
  formDetailPath,
  normalizeClientFieldName,
  resolveFormFieldsForUi,
  validationToContactRequirement,
} from "./formUtils";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "textarea", label: "Long text" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "file", label: "File upload" },
  { value: "consent_marketing", label: "Marketing consent (DPDP)" },
];

const DEFAULT_FILE_MAX_SIZE_BYTES = 5 * 1024 * 1024;

const PRESET_BUTTONS = [
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "name", label: "Name" },
  { key: "message", label: "Message" },
  { key: "text", label: "Custom text" },
  { key: "textarea", label: "Custom long text" },
  { key: "select", label: "Dropdown" },
  { key: "file", label: "File upload" },
  { key: "consent_marketing", label: "Marketing consent" },
];

function FieldEditor({ field, index, total, onChange, onMove, onRemove }) {
  const update = (patch) => onChange({ ...field, ...patch });

  return (
    <Card
      size="small"
      title={field.label || field.name || `Field ${index + 1}`}
      extra={
        <Space>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => onMove(index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === total - 1}
            onClick={() => onMove(index, 1)}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onRemove(index)}
          />
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div>
          <Text type="secondary">Label</Text>
          <Input
            value={field.label}
            onChange={(e) => {
              const label = e.target.value;
              const patch = { label };
              if (!field.nameEdited) {
                patch.name = normalizeClientFieldName(label, field.name);
              }
              update(patch);
            }}
          />
        </div>
        <div>
          <Text type="secondary">Field name (payload key)</Text>
          <Input
            value={field.name}
            onChange={(e) =>
              update({
                name: e.target.value.replace(/[^a-zA-Z0-9_]/g, ""),
                nameEdited: true,
              })
            }
          />
        </div>
        <div>
          <Text type="secondary">Type</Text>
          <Select
            style={{ width: "100%" }}
            value={field.type}
            options={FIELD_TYPE_OPTIONS}
            onChange={(type) => update({ type })}
          />
        </div>
        {field.type !== "checkbox" && field.type !== "consent_marketing" && (
          <div>
            <Text type="secondary">Placeholder</Text>
            <Input
              value={field.placeholder}
              onChange={(e) => update({ placeholder: e.target.value })}
            />
          </div>
        )}
        <Space>
          {field.type === "consent_marketing" ? (
            <Text type="secondary">
              Always unticked by default and never required — DPDP consent must
              be a free, affirmative choice.
            </Text>
          ) : (
            <Checkbox
              checked={field.required}
              onChange={(e) => update({ required: e.target.checked })}
            >
              Required
            </Checkbox>
          )}
          <Checkbox
            checked={field.showInTable}
            onChange={(e) => update({ showInTable: e.target.checked })}
          >
            Show in submissions table
          </Checkbox>
        </Space>
        {field.type === "select" && (
          <div>
            <Text type="secondary">Options (one per line: value|Label)</Text>
            <TextArea
              rows={4}
              value={(field.options || [])
                .map((o) => `${o.value}|${o.label}`)
                .join("\n")}
              onChange={(e) => {
                const options = e.target.value
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((line) => {
                    const [value, label] = line.split("|");
                    const v = (value || "").trim();
                    return {
                      value: v,
                      label: (label || v).trim() || v,
                    };
                  })
                  .filter((o) => o.value);
                update({ options });
              }}
              placeholder={"option_a|Option A\noption_b|Option B"}
            />
          </div>
        )}
        {field.type === "file" && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text type="secondary">Max file size (MB)</Text>
              <Input
                type="number"
                min={1}
                max={5}
                value={
                  (field.maxSizeBytes || DEFAULT_FILE_MAX_SIZE_BYTES) /
                  (1024 * 1024)
                }
                onChange={(e) => {
                  const mb = Number(e.target.value) || 1;
                  update({
                    maxSizeBytes: Math.min(
                      Math.max(mb, 1) * 1024 * 1024,
                      DEFAULT_FILE_MAX_SIZE_BYTES,
                    ),
                  });
                }}
              />
            </div>
            <div>
              <Text type="secondary">
                Accepted file types (comma-separated MIME types, blank = any)
              </Text>
              <Input
                value={(field.acceptedMimeTypes || []).join(", ")}
                onChange={(e) => {
                  const acceptedMimeTypes = e.target.value
                    .split(",")
                    .map((v) => v.trim().toLowerCase())
                    .filter(Boolean);
                  update({ acceptedMimeTypes });
                }}
                placeholder="image/png, image/jpeg, application/pdf"
              />
            </div>
          </Space>
        )}
      </Space>
    </Card>
  );
}

export default function FormFields() {
  const { id, isNew, formRecord, publicId, reloadForm } = useFormDetail();
  const dispatch = useDispatch();
  const [fields, setFields] = useState([]);
  const [contactRequirement, setContactRequirement] = useState("email");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!formRecord) return;
    const resolved = resolveFormFieldsForUi(formRecord).map((f, index) => ({
      ...f,
      // Legacy fields saved before this flag existed have no showInTable
      // key at all; default identity fields (already shown via the fixed
      // Email/Name/Phone columns) to checked so the UI matches reality.
      showInTable: f.showInTable ?? IDENTITY_FIELD_NAMES.has(f.name),
      order: index,
      nameEdited: true,
    }));
    setFields(resolved);
    setContactRequirement(
      validationToContactRequirement(formRecord.validation),
    );
  }, [formRecord]);

  const hostedUrl = useMemo(
    () => (publicId ? getHostedFormUrl(publicId) : ""),
    [publicId],
  );

  const addPreset = (preset) => {
    const names = fields.map((f) => f.name);
    const next = createFieldFromPreset(preset, names);
    setFields((prev) => [...prev, { ...next, order: prev.length, nameEdited: true }]);
  };

  const moveField = (index, delta) => {
    setFields((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next.map((f, i) => ({ ...f, order: i }));
    });
  };

  const handleSave = async () => {
    if (isNew) {
      message.warning("Save the form in Settings first.");
      return;
    }
    if (!fields.length) {
      message.warning("Add at least one field.");
      return;
    }

    const names = new Set();
    for (const field of fields) {
      if (!field.name?.trim()) {
        message.error("Every field needs a name.");
        return;
      }
      if (names.has(field.name)) {
        message.error(`Duplicate field name "${field.name}".`);
        return;
      }
      names.add(field.name);
    }

    if (
      formRecord?.consentMode === "double" &&
      !fields.some((f) => f.name === "email")
    ) {
      message.error(
        "Double opt-in requires an email field. Add one or switch to single opt-in in Settings.",
      );
      return;
    }

    setSaving(true);
    try {
      const payloadFields = fields.map((f, index) => ({
        id: f.id,
        name: f.name.trim(),
        type: f.type,
        label: f.label?.trim() || f.name,
        placeholder: f.placeholder || "",
        required: !!f.required,
        showInTable: !!f.showInTable,
        order: index,
        options: f.type === "select" ? f.options || [] : [],
        ...(f.type === "file"
          ? {
              maxSizeBytes: f.maxSizeBytes || DEFAULT_FILE_MAX_SIZE_BYTES,
              acceptedMimeTypes: f.acceptedMimeTypes || [],
            }
          : {}),
      }));
      await dispatch(
        updateForm(
          {
            fields: payloadFields,
            validation: contactRequirementToValidation(contactRequirement),
          },
          id,
        ),
      );
      message.success("Fields saved");
      reloadForm();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save fields",
      );
    } finally {
      setSaving(false);
    }
  };

  if (isNew) {
    return (
      <Alert
        type="info"
        showIcon
        message="Save your form first"
        description="Go to Settings, name your form, and click Save. Then you can configure fields here."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={5} style={{ marginTop: 0 }}>
          Form fields
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Define what visitors see on your hosted form and in the generated embed
          code. Submissions store every field in your inbox.
        </Paragraph>
      </div>

      {hostedUrl && (
        <Alert
          type="info"
          showIcon
          message="Hosted form link"
          description={
            <Space direction="vertical" size="small">
              <Text copyable={{ text: hostedUrl }}>{hostedUrl}</Text>
              <Button type="link" size="small" href={hostedUrl} target="_blank">
                Preview hosted form
              </Button>
            </Space>
          }
        />
      )}

      <Alert
        type="warning"
        showIcon
        message="Contacts need an email"
        description="Phone-only submissions are saved in your inbox but cannot be promoted to Contacts until a valid email is present."
      />

      {formRecord?.consentMode === "double" && (
        <Alert
          type="info"
          showIcon
          message="Double opt-in"
          description="Keep an email field on this form so visitors can confirm their submission."
        />
      )}

      <Card size="small" title="Contact requirement">
        <Select
          style={{ width: "100%", maxWidth: 420 }}
          value={contactRequirement}
          options={CONTACT_REQUIREMENT_OPTIONS}
          onChange={setContactRequirement}
        />
      </Card>

      <div>
        <Text strong>Add a field</Text>
        <Space wrap style={{ marginTop: 8 }}>
          {PRESET_BUTTONS.map((preset) => (
            <Button
              key={preset.key}
              icon={<PlusOutlined />}
              onClick={() => addPreset(preset.key)}
            >
              {preset.label}
            </Button>
          ))}
        </Space>
      </div>

      <Divider style={{ margin: "8px 0" }} />

      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {fields.map((field, index) => (
          <FieldEditor
            key={field.id || `${field.name}-${index}`}
            field={field}
            index={index}
            total={fields.length}
            onChange={(next) =>
              setFields((prev) =>
                prev.map((f, i) => (i === index ? next : f)),
              )
            }
            onMove={moveField}
            onRemove={(removeIndex) =>
              setFields((prev) =>
                prev
                  .filter((_, i) => i !== removeIndex)
                  .map((f, i) => ({ ...f, order: i })),
              )
            }
          />
        ))}
      </Space>

      <Space wrap>
        <Button type="primary" loading={saving} onClick={handleSave}>
          Save fields
        </Button>
        <Link to={formDetailPath(id, "integration")}>View embed code</Link>
      </Space>
    </Space>
  );
}
